import { useMemo } from 'react';
import { Category, DocumentsByWorkflow, Document } from '../types';
import { filterDocumentsByCategories, filterDocumentsBySearch, getDocumentsByWorkflow } from '../utils';
import { useFilterQueryParams } from '../../../../hooks/useQueryParams';

type Filters = {
  workflow: 'all' | string;
  category: Category[];
  search: string;
};

// Helper function to extract unique categories from documents
const extractCategories = (documents: Document[]): Category[] => {
  const categoryMap = new Map<number, Category>();

  documents.forEach((doc) => {
    if (doc.category) {
      if (!categoryMap.has(doc.category.id)) {
        categoryMap.set(doc.category.id, {
          id: doc.category.id,
          name: doc.category.name,
          value: doc.category.id,
          label: doc.category.name,
        });
      }
    }
  });

  return Array.from(categoryMap.values()).sort((a, b) => a.name.localeCompare(b.name));
};

export function useDocumentFilters(data: DocumentsByWorkflow) {
  const { filters, setFilter } = useFilterQueryParams<Filters>({
    workflow: 'all',
    category: [],
    search: '',
  });

  // Derive categories from documents
  const allDocuments = useMemo(() => Object.values(data).flat(), [data]);

  const availableCategories = useMemo(() => {
    if (filters.workflow === 'all') {
      // Get all unique categories from all documents
      return extractCategories(allDocuments);
    }

    // Get categories from documents in the selected workflow
    const workflowDocuments = data[filters.workflow] || [];
    return extractCategories(workflowDocuments);
  }, [filters.workflow, data, allDocuments]);

  const filteredDocuments = useMemo(() => {
    if (!data || typeof data !== 'object') {
      return [];
    }

    // Since workflows array might not exist, we'll create a simple one from the keys
    const workflows = Object.keys(data).map((name) => ({
      id: null,
      name,
      displayName: name,
      documentCount: data[name]?.length || 0,
    }));

    let docs = getDocumentsByWorkflow(
      filters.workflow,
      data,
      workflows
    );

    docs = filterDocumentsBySearch(docs, filters.search);
    const categoryIds = filters.category.map((cat) => cat.id);
    docs = filterDocumentsByCategories(docs, categoryIds);

    return docs;
  }, [filters, data]);

  const updateWorkflow = (workflowName: string) => {
    setFilter('workflow', workflowName);
    setFilter('category', []); // Reset categories when workflow changes
  };

  const updateCategories = (categories: Category[]) => {
    setFilter('category', categories);
  };

  const updateSearch = (search: string) => {
    setFilter('search', search);
  };

  return {
    filters,
    availableCategories,
    filteredDocuments,
    updateWorkflow,
    updateCategories,
    updateSearch,
  };
}
